import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtAUD } from "@/lib/api";
import { RevealLine, FadeUp } from "@/components/Reveal";
import { Check, Minus, ArrowUpRight } from "lucide-react";

const TIER_META = {
  degoogled: {
    id: "01",
    label: "Degoogled",
    color: "#888888",
    tagline: "Baseline sovereignty.",
    threat: "Casual data brokers.",
  },
  secure: {
    id: "02",
    label: "Secure",
    color: "#EAEAEA",
    tagline: "Hardened + tunnelled.",
    threat: "Targeted advertising, ISPs, opportunistic malware.",
  },
  phantom: {
    id: "03",
    label: "Phantom",
    color: "#7C3AED",
    tagline: "Physically re-engineered.",
    threat: "Nation states, forensic investigators, physical seizure.",
  },
};

// Master feature matrix — canonical for the comparison table
const FEATURES = [
  ["Google Play Services removed", "degoogled"],
  ["F-Droid preinstalled", "degoogled"],
  ["Encrypted DNS", "degoogled"],
  ["Verified boot", "degoogled"],
  ["Sandboxed apps", "degoogled"],
  ["Hardened memory allocator (hardened_malloc)", "secure"],
  ["Always-on Mullvad VPN + kill switch", "secure"],
  ["Sensor block in background", "secure"],
  ["Molly (hardened Signal fork)", "secure"],
  ["Aegis 2FA + OnionShare", "secure"],
  ["Duress PIN wipe", "secure"],
  ["Faraday sleeve included", "secure"],
  ["Physical microphone removal", "phantom"],
  ["USB data pin severed (power-only)", "phantom"],
  ["Per-app user profile isolation", "phantom"],
  ["Location services hardware-blocked", "phantom"],
  ["Custom locked bootloader", "phantom"],
  ["Hardware attestation module", "phantom"],
  ["Titanium Faraday shell option", "phantom"],
  ["Concierge onboarding + audit logs", "phantom"],
];

const TIER_ORDER = ["degoogled", "secure", "phantom"];

function has(tier, minTier) {
  return TIER_ORDER.indexOf(tier) >= TIER_ORDER.indexOf(minTier);
}

export default function Compare() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const cheapest = (t) => {
    if (!products.length) return null;
    // find cheapest variant across all phones at this tier
    let best = null;
    for (const p of products) {
      for (const v of (p.variants || [])) {
        if (v.tier === t && (!best || v.price_aud < best.price)) {
          best = { price: v.price_aud, slug: p.slug, name: p.name };
        }
      }
    }
    return best ? { price_aud: best.price, slug: best.slug, name: best.name } : null;
  };

  return (
    <div className="pt-32 pb-24 px-6 md:px-10" data-testid="compare-page">
      <div className="overline mb-6">Ξ 011 — Comparison Matrix</div>
      <h1 className="font-display uppercase text-5xl md:text-8xl tracking-tight leading-[0.9]">
        <RevealLine i={0}>Three tiers.</RevealLine>
        <RevealLine i={1}>Twenty controls.</RevealLine>
        <RevealLine i={2}>Pick your posture.</RevealLine>
      </h1>

      <FadeUp delay={0.2}>
        <p className="mt-10 max-w-2xl text-[#888] leading-relaxed">
          Each tier includes everything from the tier below it. Use the matrix to identify which controls
          match your threat model, then pick the cheapest device inside that band.
        </p>
      </FadeUp>

      {/* Header tier cards */}
      <div className="mt-20 grid grid-cols-12 gap-0 border border-[#1A1A1A]">
        <div className="col-span-12 md:col-span-3 p-6 md:border-r border-[#1A1A1A] hidden md:block">
          <div className="overline">Control</div>
        </div>
        {TIER_ORDER.map((t, idx) => {
          const meta = TIER_META[t];
          const p = cheapest(t);
          return (
            <div
              key={t}
              className={`col-span-12 md:col-span-3 p-6 md:p-8 border-b border-[#1A1A1A] md:border-b-0 ${
                idx < 2 ? "md:border-r" : ""
              } ${t === "phantom" ? "bg-[#080808]" : ""}`}
            >
              <div className="flex items-center justify-between">
                <div className="overline">Tier {meta.id}</div>
                <span className={`tier-dot ${t}`} />
              </div>
              <div className="font-display uppercase text-4xl md:text-5xl mt-6 tracking-tight" style={{ color: meta.color }}>
                <span className="block text-xs md:text-sm text-[#888] tracking-[0.35em] mb-2">PHANTOM</span>
                {meta.label}
              </div>
              <div className="mt-3 text-[#EAEAEA] text-sm">{meta.tagline}</div>
              <div className="mt-6">
                <div className="overline text-[#444]">Threat model</div>
                <div className="text-[#888] text-sm mt-2">{meta.threat}</div>
              </div>
              <div className="mt-8 border-t border-[#1A1A1A] pt-6">
                <div className="overline text-[#444]">From</div>
                <div className="font-display text-3xl mt-2">{p ? fmtAUD(p.price_aud) : "—"}</div>
                {p && (
                  <Link
                    to={`/product/${p.slug}`}
                    data-testid={`compare-cta-${t}`}
                    className={`btn-vault mt-6 w-full justify-center ${t === "phantom" ? "danger" : ""}`}
                  >
                    View {p.name.split("//")[0].trim()} <ArrowUpRight size={14} />
                  </Link>
                )}
                {t === "phantom" && (
                  <Link
                    to="/hardening"
                    data-testid="compare-hardening-link"
                    className="mt-3 overline flex items-center gap-2 text-[#7C3AED] hover:text-[#EAEAEA] transition-colors"
                  >
                    Read the Phantom Hardening audit →
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Matrix */}
      <div className="mt-16 border border-[#1A1A1A]" data-testid="compare-matrix">
        {FEATURES.map(([label, minTier], i) => (
          <div
            key={label}
            className={`grid grid-cols-12 gap-0 border-b border-[#1A1A1A] last:border-b-0 ${
              i % 2 === 1 ? "bg-[#070707]" : ""
            } hover:bg-[#0A0A0A] transition-colors`}
          >
            <div className="col-span-12 md:col-span-3 p-4 md:p-5 md:border-r border-[#1A1A1A]">
              <div className="text-[#EAEAEA] text-sm">{label}</div>
              <div className="overline text-[#444] mt-1 md:hidden">requires: tier {TIER_META[minTier].id}+</div>
            </div>
            {TIER_ORDER.map((t, idx) => {
              const active = has(t, minTier);
              const color = TIER_META[t].color;
              return (
                <div
                  key={t}
                  className={`col-span-4 md:col-span-3 p-4 md:p-5 flex items-center justify-center ${
                    idx < 2 ? "md:border-r" : ""
                  } border-r border-[#1A1A1A] md:border-r`}
                >
                  {active ? (
                    <Check size={18} style={{ color }} strokeWidth={1.8} data-testid={`match-${t}-${i}`} />
                  ) : (
                    <Minus size={18} className="text-[#333]" strokeWidth={1.5} />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-16 text-center">
        <Link to="/shop" className="btn-vault">Browse full catalogue <ArrowUpRight size={14} /></Link>
      </div>
    </div>
  );
}
